import { cx } from "../../lib/cx.js";
import { IconButton } from "../IconButton/IconButton.js";
import { addDays, addMonths, isBeforeDay, isSameDay, isSameMonth, monthGrid, startOfDay, startOfMonth, startOfWeek } from "./dates.js";
/* empty css             */
import { useEffect, useId, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Calendar/Calendar.tsx
var MONTH_TITLE = new Intl.DateTimeFormat("en-US", {
	month: "long",
	year: "numeric"
});
var DAY_LABEL = new Intl.DateTimeFormat("en-US", {
	weekday: "long",
	month: "long",
	day: "numeric",
	year: "numeric"
});
var WEEKDAY_SHORT = new Intl.DateTimeFormat("en-US", { weekday: "short" });
var WEEKDAY_LONG = new Intl.DateTimeFormat("en-US", { weekday: "long" });
/**
* One month as a date grid (APG): arrows move by day and week, Page Up/Down by month
* (with Shift, by year), Home/End to the week's ends, Enter or Space picks. Controlled
* or uncontrolled month; single dates or a range with a hover preview.
*/
function Calendar({ month: monthProp, defaultMonth, onMonthChange, selected = null, rangeStart = null, rangeEnd = null, hovered = null, onHover, onSelect, min = null, max = null, showHeader = true, navPrev = true, navNext = true, weekStartsOn = 1, today: todayProp, showOutsideDays = true, className }) {
	const [initialToday] = useState(() => startOfDay(/* @__PURE__ */ new Date()));
	const shownToday = todayProp ? startOfDay(todayProp) : initialToday;
	const [monthState, setMonthState] = useState(() => startOfMonth(defaultMonth ?? selected ?? rangeStart ?? shownToday));
	const month = startOfMonth(monthProp ?? monthState);
	const setMonth = (next) => {
		const first = startOfMonth(next);
		if (monthProp === void 0) setMonthState(first);
		onMonthChange?.(first);
	};
	const titleId = `${useId()}-title`;
	const weeks = monthGrid(month, weekStartsOn);
	const gridRef = useRef(null);
	const [focusedDay, setFocusedDay] = useState(null);
	const inMonth = (day) => !!day && isSameMonth(day, month);
	const tabDay = inMonth(focusedDay) && focusedDay || inMonth(selected) && selected || inMonth(rangeStart) && rangeStart || inMonth(shownToday) && shownToday || month;
	const pendingFocus = useRef(false);
	useEffect(() => {
		if (!pendingFocus.current) return;
		pendingFocus.current = false;
		gridRef.current?.querySelector("[tabindex=\"0\"]")?.focus();
	});
	const isDisabled = (day) => !!min && isBeforeDay(day, min) || !!max && isBeforeDay(max, day);
	const isRange = rangeStart !== null || rangeEnd !== null;
	const previewEnd = rangeEnd ?? hovered;
	let low = rangeStart;
	let high = previewEnd;
	if (low && high && isBeforeDay(high, low)) [low, high] = [high, low];
	const moveFocus = (day) => {
		pendingFocus.current = true;
		setFocusedDay(day);
		if (!isSameMonth(day, month)) setMonth(day);
		onHover?.(day);
	};
	const handleKeyDown = (event, day) => {
		const move = {
			ArrowLeft: () => addDays(day, -1),
			ArrowRight: () => addDays(day, 1),
			ArrowUp: () => addDays(day, -7),
			ArrowDown: () => addDays(day, 7),
			Home: () => startOfWeek(day, weekStartsOn),
			End: () => addDays(startOfWeek(day, weekStartsOn), 6),
			PageUp: () => addMonths(day, event.shiftKey ? -12 : -1),
			PageDown: () => addMonths(day, event.shiftKey ? 12 : 1)
		}[event.key];
		if (!move) return;
		event.preventDefault();
		moveFocus(move());
	};
	const pick = (day) => {
		if (isDisabled(day)) return;
		setFocusedDay(day);
		onSelect?.(startOfDay(day));
	};
	return /* @__PURE__ */ jsxs("div", {
		className: cx("ds-calendar", className),
		children: [showHeader && /* @__PURE__ */ jsxs("div", {
			className: "ds-calendar__header",
			children: [
				navPrev ? /* @__PURE__ */ jsx(IconButton, {
					icon: "chevron-left",
					size: "sm",
					"aria-label": "Previous month",
					className: "ds-calendar__nav",
					onClick: () => setMonth(addMonths(month, -1))
				}) : /* @__PURE__ */ jsx("span", { className: "ds-calendar__nav-space" }),
				/* @__PURE__ */ jsx("h2", {
					id: titleId,
					className: "ds-calendar__title",
					"aria-live": "polite",
					children: MONTH_TITLE.format(month)
				}),
				navNext ? /* @__PURE__ */ jsx(IconButton, {
					icon: "chevron-right",
					size: "sm",
					"aria-label": "Next month",
					className: "ds-calendar__nav",
					onClick: () => setMonth(addMonths(month, 1))
				}) : /* @__PURE__ */ jsx("span", { className: "ds-calendar__nav-space" })
			]
		}), /* @__PURE__ */ jsxs("table", {
			ref: gridRef,
			role: "grid",
			className: "ds-calendar__grid",
			"aria-labelledby": showHeader ? titleId : void 0,
			"aria-label": showHeader ? void 0 : MONTH_TITLE.format(month),
			onMouseLeave: () => onHover?.(null),
			children: [/* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { children: weeks[0].map((day) => /* @__PURE__ */ jsx("th", {
				scope: "col",
				abbr: WEEKDAY_LONG.format(day),
				className: "ds-calendar__weekday",
				children: WEEKDAY_SHORT.format(day).slice(0, 2)
			}, day.getDay())) }) }), /* @__PURE__ */ jsx("tbody", { children: weeks.map((week) => /* @__PURE__ */ jsx("tr", { children: week.map((day) => {
				const disabled = isDisabled(day);
				const outside = !isSameMonth(day, month);
				const start = isRange && isSameDay(day, rangeStart);
				const end = isRange && (isSameDay(day, rangeEnd) || !rangeEnd && isSameDay(day, hovered));
				const between = isRange && !!low && !!high && !isBeforeDay(day, low) && !isBeforeDay(high, day);
				const isSelected = isRange ? start || end : isSameDay(day, selected);
				const isToday = isSameDay(day, shownToday);
				if (outside && !showOutsideDays) return /* @__PURE__ */ jsx("td", {
					role: "gridcell",
					className: "ds-calendar__cell"
				}, day.getTime());
				return /* @__PURE__ */ jsx("td", {
					role: "gridcell",
					"aria-selected": isSelected,
					className: cx("ds-calendar__cell", between && "ds-calendar__cell--between", (start || between && low && isSameDay(day, low)) && "ds-calendar__cell--start", (end || between && high && isSameDay(day, high)) && "ds-calendar__cell--end"),
					children: /* @__PURE__ */ jsx("button", {
						type: "button",
						tabIndex: isSameDay(day, tabDay) ? 0 : -1,
						"aria-label": DAY_LABEL.format(day),
						"aria-current": isToday ? "date" : void 0,
						"aria-disabled": disabled || void 0,
						className: cx("ds-calendar__day", outside && "ds-calendar__day--outside", isToday && "ds-calendar__day--today", isSelected && "ds-calendar__day--selected", between && !isSelected && "ds-calendar__day--between", disabled && "ds-calendar__day--disabled"),
						onClick: () => pick(day),
						onKeyDown: (event) => {
							if (event.key === "Enter" || event.key === " ") return;
							handleKeyDown(event, day);
						},
						onMouseEnter: () => onHover?.(day),
						onFocus: () => {
							setFocusedDay(day);
							onHover?.(day);
						},
						children: day.getDate()
					})
				}, day.getTime());
			}) }, week[0].getTime())) })]
		})]
	});
}
//#endregion
export { Calendar };

//# sourceMappingURL=Calendar.js.map